<template>
  <div class="relative">
    <div class="flex items-center">
      <div
        class="w-full h-[38px] px-3 py-2 flex flex-row-reverse items-center bg-gray-800/5 tablet-h:bg-white rounded-sm tablet-h:border border-gray-800/25 focus-within:border-gray-800/35 relative"
        @click="handleFocus"
      >
        <input
          v-model="query"
          type="text"
          class="peer w-full h-full bg-transparent outline-hidden font-primary text-[14px] leading-[18px] text-gray-800 placeholder:text-gray-800/50 pr-4"
          placeholder="Search"
          ref="input"
          :class="inputClasses"
          @focus="handleFocus"
          @keydown.esc="handleBlur"
        />
        <Icon
          :size="14"
          name="Search"
          class="stroke-gray-800 mr-2.5 peer-placeholder-shown:opacity-40 peer-focus:opacity-100"
        />
      </div>
    </div>
    <div
      v-if="inputFocused"
      class="search--results absolute left-0 top-full w-full bg-white z-100 rounded-md overflow-y-auto overflow-hidden"
    >
      <SearchTable
        v-if="showResults"
        :data="results"
        :query="query"
        @clicked="handleClick"
      />
      <div
        v-if="query.length === 1"
        class="w-full h-[60px] flex items-center justify-center font-secondary text-[15px] leading-[18px] text-gray-800 opacity-50"
      >
        Your search must be at least 2 characters long
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  useSearchLazyQuery,
  type SearchResult,
} from '@/shared/graphql/legacy/generated'
import SearchTable from '@/features/search/components/SearchTable.vue'
import { useDevice } from '@/shared/composables/useDevice'
import { Icon } from '@dacr/ui'
import { useMobileSearchStore } from '@/features/search/store'

const device = useDevice()
const mobileSearchStore = useMobileSearchStore()

const query = ref('')
const inputFocused = ref(false)
const input = ref()

const variables = computed(() => ({ query: query.value }))

const { result, load } = useSearchLazyQuery(variables)

watch(query, () => {
  if (query.value.length > 1) {
    load()
  }
})

const inputClasses = computed(() => ({
  'pointer-events-none': device.isMobile.value || device.isTabletVertical.value,
}))

const results = computed(() => result.value?.search as SearchResult | undefined)
const showResults = computed(() => inputFocused.value && query.value.length > 1)

const handleFocus = () => {
  if (device.isMobile.value || device.isTabletVertical.value) {
    mobileSearchStore.showSearch()
  } else {
    inputFocused.value = true
  }
}

const handleBlur = () => {
  inputFocused.value = false
  input.value.blur()
}

const handleClick = () => {
  handleBlur()
  query.value = ''
}

const handleOutsideClick = (event: Event) => {
  const target = event.target as HTMLElement
  if (!(input.value as HTMLDivElement).contains(target)) {
    handleBlur()
  }
}

onMounted(() => {
  if (device.isMobile.value) return
  window.addEventListener('click', handleOutsideClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('click', handleOutsideClick)
})
</script>

<style scoped>
@media (min-width: 1023px) {
  .search--results {
    box-shadow:
      0 0 8px #0000001a,
      0 2px 4px #00000033;
  }
}
</style>
